/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import cn from "classnames";
import { memo } from "react";
import AudioPulse from "../audio-pulse/AudioPulse";
import "./control-tray.scss";
import TAAudioAgent from "ta-agent-sdk";
import { KeyboardIcon } from "../Icons";
import { useTranslation } from "react-i18next";

const ControlTray = ({
  connected,
  agent,
  muted,
  setShowChat,
}: {
  connected: boolean;
  agent: TAAudioAgent;
  muted: boolean;
  setShowChat: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { t } = useTranslation();
  async function handleMicClick() {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      try {
        await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: 16000,
            channelCount: 1,
            // Enhanced noise reduction
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            // Additional constraints
            // @ts-ignore
            googEchoCancellation: true,
            // @ts-ignore
            googAutoGainControl: true,
            // @ts-ignore
            googNoiseSuppression: true,
            // @ts-ignore
            googHighpassFilter: true,
            // @ts-ignore
            googTypingNoiseDetection: true,
            // @ts-ignore
            googBeamforming: true,
            // @ts-ignore
            googArrayGeometry: true,
            // @ts-ignore
            googAudioMirroring: false,
            // @ts-ignore
            mozNoiseSuppression: true,
            // @ts-ignore
            mozAutoGainControl: true,
          },
        });
        agent.toggleMic();
      } catch (error) {
        alert(t("controlTray.micAccessError"));
      }
    } else {
      console.error("getUserMedia not supported");
    }
  }
  return (
    <div className="control-tray">
      <div className={cn("connection-container", { connected })}>
        <button
          className={cn("action-button shadow-lg shadow-neutral-500", {
            connected,
          })}
          id="restartButton"
          onClick={() => window.location.reload()}
        >
          {/* <span
            className="helper"
            style={{ textShadow: "0 0 10px rgb(0, 0, 0)" }}
          >
            Restart
          </span> */}
          <img
            src="https://d1mdu1cxiv2wrk.cloudfront.net/nyc/images/wave-sound@2x.png"
            alt="restart"
          />
        </button>
        <button
          className={cn("action-button connect-toggle mic-button", {
            connected,
            muted,
            "shadow-lg shadow-neutral-500": connected,
          })}
          onClick={handleMicClick}
        >
          <span className="main">Start</span>
          <img
            src="https://d1mdu1cxiv2wrk.cloudfront.net/nyc/images/mic2.svg"
            alt="mic"
          />
        </button>
        <div className="flex flex-col gap-2">
          <div
            id="pulse"
            className={cn(
              "action-button no-action shadow-lg shadow-neutral-500",
              { connected }
            )}
          >
            <span
              className="helper"
              style={{ textShadow: "0 0 10px rgb(0, 0, 0)" }}
            >
              {t("controlTray.resume")}
            </span>
            <AudioPulse />
          </div>
          <div
            className={cn(
              "action-button md:!hidden shadow-lg shadow-neutral-500",
              {
                connected,
              }
            )}
            onClick={() => setShowChat((prev) => !prev)}
          >
            <KeyboardIcon />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(ControlTray);
